import { forwardRef, Popover, StyleProps } from '@chakra-ui/react';
import { PropsWithChildren, ReactElement } from 'react';

import { CalendarOutline } from '@bonusly/ds-icon';

import { useDateRangePicker } from '../../hooks/use-date-range-picker';
import { DateRange } from '../../types';
import { Calendar } from '../calendar/calendar';
import { CalendarControls } from '../calendar/calendar-controls';
import { CalendarFooter } from '../calendar/calendar-footer';
import { CalendarRangeHeader } from '../calendar/calendar-range-header';
import { DatePickerContext } from '../context';
import { DateRangeInput } from './date-range-input';
import { DateRangePopup } from './date-range-popup';

export type DateRangePickerProps = PropsWithChildren<
	StyleProps & {
		displayFormat?: 'short' | 'long';
		isClearable?: boolean;
		leftIcon?: ReactElement;
		onChange: (value: DateRange) => void;
		value: DateRange;
	}
>;

export const DateRangePicker = forwardRef<DateRangePickerProps, 'button'>(
	({ children, displayFormat = 'short', isClearable = true, leftIcon = <CalendarOutline />, onChange, value, ...rest }, ref) => {
		const picker = useDateRangePicker({ onChange, value });

		if (children) {
			return children;
		}

		return (
			<DatePickerContext value={{ displayFormat, isClearable, value, ...picker }}>
				<Popover isLazy isOpen={picker.isOpen} onClose={picker.onClose}>
					<DateRangeInput leftIcon={leftIcon} ref={ref} {...rest} />

					<DateRangePopup p={4}>
						<CalendarControls mb={1} />
						<CalendarRangeHeader mb={1} />
						<Calendar mb={2} />
						<CalendarFooter />
					</DateRangePopup>
				</Popover>
			</DatePickerContext>
		);
	}
);

DateRangePicker.displayName = 'DateRangePicker';
